<template>
  <div class="home">
    <object-creater
      v-if="isNewObjectCreate"
      :maxWidth="modalMidleWidth"
      :maxHeight="modalMaxHeight"
      :countries="countries"
      :cities="cities"
      :objectTypes="virtualObjectTypes"
      :show="isNewObjectCreate"
      @close="newObjectFormClose"
      @save="newObjectFormSave"
    ></object-creater>

    <text-editor
      v-if="showTextEditor"
      :maxWidth="modalMaxWidth"
      :maxHeight="modalMaxHeight"
      :show="showTextEditor"
      :title="titleMemo"
      @close="closeAttrModal"
      @save="saveTextEditor"
    />

    <attribute-value-coordinate
      v-if="showAttrModalCoord"
      :maxWidth="modalMidleWidth"
      :maxHeight="modalMaxHeight"
      :show="showAttrModalCoord"
      :title="$t('attr_value_coord_title')"
      :complexValue="complexValue"
      @close="closeAttrModal"
      @save="saveTextEditor"
    />

    <attribute-value-gallery-new
      v-if="showAttrModalGallery"
      v-bind="modalSizeGallery"
      :show="showAttrModalGallery"
      :previewImageUrl="previewImgUrl"
      :fileName="fileName"
      :languages="languages"
      :widthImgage="widthImgage"
      :heightImage="heightImage"
      :galleryNames="galleryNames"
      :nextOrderNum="nextOrderNum"
      :mode="galleryMode"
      :editableObject="editableGalleryObject"
      @close="closeAttrModal"
      @preview="clickImagePreview"
      @saveForm="saveGalleryForm"
    />

    <object-info
      v-if="showModalInfo"
      :maxWidth="modalMaxWidth"
      :minWidth="modalMaxWidth"
      :maxHeight="modalMaxHeight"
      :show="showModalInfo"
      :info="aboutItems"
      :name="object.name"
      @close="closeAttrModal"
    />

    <object-filter
      class="panel home__filter"
      :countries="countriesItems"
      :cities="citiesFilterItems"
      :objectTypes="usedObjectTypes"
      :filter="filters"
      @change="changeFilter"
    />

    <div class="home__content">
      <div class="panel home__content__object">
        <app-sidebar :title="title" :items="navigation" :loading="loading">
          <template v-slot:actionTop>
            <div class="home__content__object_action">
              <el-button
                type="success"
                icon="el-icon-circle-plus-outline"
                :title="$t('object_create_title')"
                @click="addObject"
              >
                {{ $t("object_create") }}
              </el-button>
              <el-button
                type="danger"
                icon="el-icon-delete"
                plain
                :title="$t('object_remove_title')"
                :disabled="disableRemoveObject"
                @click="deleteObject"
              >
                {{ $t("object_remove") }}
              </el-button>
            </div>
          </template>
        </app-sidebar>
      </div>

      <div v-show="!copyPhotoMode" class="home__content__info">
        <div v-if="isObject" class="panel home__content__info-about">
          <ObjectCard
            :objectCard="objectCard"
            :languages="objectLanguages"
            :countries="countriesItems"
            :cities="citiesObjectItems"
            :isVirtual="isVirtualObject"
            :loading="loading"
            :lock="lockCard"
            @changeCard="changeCard"
          />
        </div>

        <div v-if="isObject" class="panel home__content__info-attr">
          <transition name="fade">
            <object-attribute
              v-if="mode === MODE_ATTR"
              :attributes="objectAttributeList"
              :languagesUsed="object.languagesUsed"
              :references="referenceCache"
              :languages="objectLanguages"
              :loading="loading"
              :objectCard="objectCard"
              @action="actionAttribute"
              @delete="deleteAttribute"
              @changeCurrentLanguage="changeCurrentLanguage"
              @showInfo="showObjectInfo"
              @changeCard="changeCard"
            />

            <object-program
              v-if="mode === MODE_PROGRAM"
              :languages="objectLanguages"
              :loading="loading"
              :cities="cities"
              @changeProgram="changeProgram"
              @cancel="cancelProgram"
              @save="saveProgram"
              @changeCurrentLanguage="changeCurrentLanguage"
            />

            <object-gallery
              v-if="mode === MODE_GALLERY"
              :languages="objectLanguages"
              :loading="loading"
              @create="createGallery"
              @save="saveGallery"
              @cancel="cancelGallery"
              @change="changeGallery"
              @copy="copyGalleryPhoto"
              @changeCurrentLanguage="changeCurrentLanguage"
            />

            <object-attachment
              v-if="mode === MODE_ATTACHMENT"
              :loading="loading"
              :languages="languages"
              @save="saveAttachment"
              @cancel="cancelAttachment"
            />
          </transition>
        </div>

        <div v-if="!isObject" class="panel home__content__no-object">
          <no-set-object />
        </div>
      </div>

      <div v-if="copyPhotoMode" class="home__content__info">
        <div class="panel home__content__info_copy">
          <object-copy-photo
            :object="objectCopy"
            :usedNames="usedNamesCopy"
            :loading="loading"
            @cancel="cancelCopyObject"
            @copy="copyObjectFiles"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppSidebar from "../components/library.blocks/AppSidebar.vue";
import ObjectFilter from "../components/common.blocks/objects/ObjectFilter.vue";
import ObjectCard from "../components/common.blocks/objects/ObjectCard.vue";
import ObjectAttribute from "../components/common.blocks/objects/ObjectAttribute.vue";

import { mapState, mapGetters } from "vuex";
import { Response, Files, System, TextEditor } from "@/mixins";

import { CREATE, UPDATE, DELETE } from "@/domain/crud.types";
import { clone } from "@/domain/system/utils";
import { MODE_NEW, MODE_EDIT } from "@/domain/mode.types";

import * as service from "@/domain/object/object.service";
import * as systemService from "@/domain/object/system.service";
import * as programService from "@/domain/object/program.service";
import * as galleryService from "@/domain/object/gallery.service";
import * as attachmentService from "@/domain/object/attachment.service";

import {
  OBJECT_TYPES_GET,
  LANGUAGE_REFS,
  COUNTRY_REFS,
  CITY_REFS,
  OBJECTS_LIST_READ,
  OBJECTS_READ,
  OBJECTS_COPY_READ,
  OBJECT_TEMPLATES_READ,
  OBJECTS_DELETE,
  OBJECTS_CLEAR,
  OBJECTS_COPY_CLEAR,
  OBJECTS_CREATE,
  OBJECTS_UPDATE,
  OBJECTS_FILTER_GET,
  OBJECTS_FILTER_SAVE,
  OBJECTS_NAVIGATION_SET,
  OBJECTS_NAVIGATION_SELECT,
  ATTR_VALUE_CREATE,
  ATTR_VALUE_UPDATE,
  ATTR_VALUE_DELETE,
  REFERENCE_SPECIFIC,
  OBJECTS_INFO
} from "@/store/type";

export default {
  name: "home",
  components: {
    AppSidebar,
    ObjectFilter,
    ObjectCard,
    ObjectAttribute,
    ObjectCreater: () =>
      import(
        /* webpackChunkName: "objectCreater" */ "../components/common.blocks/objects/ObjectCreater.vue"
      ),
    TextEditor: () =>
      import(
        /* webpackChunkName: "textEditor" */ "@/components/common.blocks/attributeValue/TextEditor.vue"
      ),
    AttributeValueCoordinate: () =>
      import(
        /* webpackChunkName: "attributeValueCoordinate" */ "@/components/common.blocks/attributeValue/AttrCoordinate.vue"
      ),
    AttributeValueGalleryNew: () =>
      import(
        /* webpackChunkName: "attributeValueGalleryNew" */ "@/components/common.blocks/attributeValue/AttrGalleryNew.vue"
      ),
    ObjectProgram: () =>
      import(
        /* webpackChunkName: "objectProgram" */ "@/components/common.blocks/objects/ObjectProgram.vue"
      ),
    ObjectGallery: () =>
      import(
        /* webpackChunkName: "objectGallery" */ "@/components/common.blocks/objects/ObjectGallery.vue"
      ),
    ObjectInfo: () =>
      import(
        /* webpackChunkName: "objectInfo" */ "@/components/common.blocks/objects/ObjectInfo.vue"
      ),
    ObjectCopyPhoto: () =>
      import(
        /* webpackChunkName: "objectCopyPhoto" */ "@/components/common.blocks/objects/ObjectCopyPhoto.vue"
      ),
    ObjectAttachment: () =>
      import(
        /* webpackChunkName: "objectAttachment" */ "@/components/common.blocks/objects/ObjectAttachment.vue"
      ),
    NoSetObject: () =>
      import(
        /* webpackChunkName: "noSetObject" */ "@/components/common.blocks/information/NoSetObject.vue"
      )
  },
  mixins: [Response, Files, System, TextEditor],
  data() {
    return {
      referenceLoading: [
        { mutation: OBJECT_TYPES_GET, refs: "objectTypes" },
        { mutation: LANGUAGE_REFS, refs: "languages" },
        { mutation: COUNTRY_REFS, refs: "countries" },
        { mutation: CITY_REFS, refs: "cities" },
        { mutation: OBJECTS_LIST_READ, refs: "objectsList" }
      ],
      actionsCRUD: [
        { action: CREATE, mutation: ATTR_VALUE_CREATE },
        { action: UPDATE, mutation: ATTR_VALUE_UPDATE },
        { action: DELETE, mutation: ATTR_VALUE_DELETE }
      ],
      filters: {
        name: undefined,
        countryId: undefined,
        cityId: undefined,
        type: undefined,
        content: undefined
      },
      modalMaxWidth: "1200px",
      modalMidleWidth: "400px",
      modalMaxHeight: "100vh",
      loading: false,
      isNewObjectCreate: false,
      showTextEditor: false,
      showAttrModalCoord: false,
      showAttrModalGallery: false,
      showModalInfo: false,
      titleMemo: "Memo",
      attributeObj: undefined,
      MODE_ATTR: 0,
      MODE_PROGRAM: 1,
      MODE_GALLERY: 2,
      MODE_ATTACHMENT: 3,
      mode: 0,
      galleryMode: MODE_NEW,
      editableGalleryObject: {},
      complexValue: {},
      copyPhotoMode: false,
      mainObjectId: undefined
    };
  },
  computed: {
    ...mapState({
      lang: state => state.settings.lang,
      objectTypes: state => state.objectTypes.objectTypes,
      languages: state => state.references.languages,
      countries: state => state.references.countries,
      cities: state => state.references.cities,
      objectsList: state => state.objects.objectsList,
      objectsListNavigation: state => state.objects.objectsListNavigation,
      object: state => state.objects.object,
      objectCopy: state => state.objects.objectCopy,
      objectFilter: state => state.objects.filter,
      objectTemplates: state => state.objectTemplates.objectTemplates,
      referenceCache: state => state.references.referenceCache,
      info: state => state.objects.info,
      currentAtributeLang: state => state.objectSetting.currentLanguage,
      gallery: state => state.objectComplexValue.galleryList.gallery,
      urlEditable: state => state.objectComplexValue.urlEditable
    }),
    ...mapGetters([
      "virtualObjectTypes",
      "usedObjectTypes",
      "getReferenceSpecific"
    ]),
    navigation() {
      if (this.filters.name) {
        let nm = this.filters.name.toUpperCase();
        return this.objectsListNavigation.filter(n =>
          n.name ? n.name.toUpperCase().includes(nm) : false
        );
      }
      return this.objectsListNavigation;
    },
    title() {
      return this.$t("object_sidebar_title", {
        count: this.navigation.length
      });
    },
    countriesItems() {
      return service.getCountryRefs(
        this.countries,
        this.$t("object_country_empty")
      );
    },
    citiesFilterItems() {
      return service.getCiyRefs(
        this.cities,
        this.$t("object_city_empty"),
        this.filters.countryId
      );
    },
    citiesObjectItems() {
      return service.getCiyRefs(
        this.cities,
        this.$t("object_city_empty"),
        this.object.countryId > 0 ? this.object.countryId : undefined
      );
    },
    objectCard() {
      return service.getObjectCard(this.object, this.objectTypes);
    },
    disableRemoveObject() {
      return !service.canBeDeleted(this.virtualObjectTypes, this.object);
    },
    defaultLangId() {
      if (this.languages.length) {
        let item = this.languages.find(lng => lng.default === true);
        if (item) {
          return item.id;
        }
      }

      return "";
    },
    objectAttributeList() {
      if (
        this.objectTemplates &&
        this.objectTemplates.length &&
        this.object &&
        this.currentAtributeLang &&
        this.defaultLangId
      ) {
        return service.objectAttributes(
          this.objectTemplates,
          this.object.data || [],
          this.currentAtributeLang,
          this.defaultLangId,
          this.object.done
        );
      }
      return [];
    },
    isObject() {
      return this.object && this.object.id;
    },
    isVirtualObject() {
      return service.isVirtual(this.object, this.objectTypes);
    },
    objectLanguages() {
      return service.getLanguages(
        this.object,
        this.languages,
        this.objectTemplates
      );
    },
    galleryNames() {
      return galleryService.getFileNames(this.gallery);
    },
    nextOrderNum() {
      return galleryService.getNextNumberInOrderNum(this.gallery);
    },
    modalSizeGallery() {
      if (this.galleryMode === MODE_NEW) {
        return {
          maxWidth: this.modalMaxWidth,
          minWidth: this.modalMaxWidth,
          maxHeight: this.modalMaxHeight
        };
      }
      return {
        maxWidth: this.modalMidleWidth,
        maxHeight: this.modalMaxHeight
      };
    },
    usedNamesCopy() {
      let result = [];
      if (this.complexValue) {
        result = galleryService.getUsedCopyNames(this.complexValue);
      }
      return result;
    },
    lockCard() {
      return this.mode !== this.MODE_ATTR;
    },
    aboutItems() {
      return this.info.filter(t => !systemService.isFile(t.type));
    }
  },
  methods: {
    async setNavigation() {
      let path = this.$route.path;
      let id = this.$route.query.object
        ? Number(this.$route.query.object)
        : undefined;

      let result = service.objectToFiltersNavigation(
        this.objectsList,
        this.filters,
        this.usedObjectTypes,
        path,
        id,
        this.mainObjectId
      );

      this.$store.commit(OBJECTS_NAVIGATION_SET, result);
    },
    getReferenceLoading() {
      this.referenceLoading.forEach(item => {
        if (this[item.refs].length === 0) {
          this.$store.dispatch(item.mutation, this.lang);
        }
      });
    },
    changeFilter(val) {
      if (this.$route.query.object && !this.copyPhotoMode) {
        this.$store.commit(OBJECTS_CLEAR);
        this.$router.replace({ object: null });
      }

      this.filters.name = val.name;
      this.filters.countryId = val.countryId;
      this.filters.cityId = val.cityId;
      this.filters.type = val.type;
      this.filters.content = val.content;

      this.saveFilterStor({
        countryId: this.filters.countryId,
        cityId: this.filters.cityId,
        type: this.filters.type,
        content: this.filters.content
      });

      this.setNavigation();
    },
    resetCopyObject() {
      if (this.copyPhotoMode) {
        this.copyPhotoMode = false;
      }

      if (this.mainObjectId) {
        this.mainObjectId = undefined;
        this.setNavigation();
        this.$store.commit(OBJECTS_COPY_CLEAR);
        this.restoreMode();
      }
    },
    uploadCopyObject() {
      this.$store.commit(OBJECTS_COPY_CLEAR);

      let objId = this.$route.query.copy_object;
      if (objId) {
        this.loading = true;
        this.selectedObject(Number(objId));

        this.$store
          .dispatch(OBJECTS_COPY_READ, { id: objId, lang: this.lang })
          .then(() => {
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
          });
      }
    },
    uploadObject() {
      let objId = this.$route.query.object;
      if (objId) {
        this.loading = true;
        this.selectedObject(Number(objId));

        this.$store
          .dispatch(OBJECTS_READ, { id: objId, lang: this.lang })
          .then(resp => {
            if (resp.typesOfObjectsId) {
              this.$store
                .dispatch(OBJECT_TEMPLATES_READ, {
                  objectTypesId: resp.typesOfObjectsId,
                  lang: this.lang
                })
                .then(resp => {
                  service.getReferenceSpecificListId(resp).forEach(id => {
                    this.$store.dispatch(REFERENCE_SPECIFIC, {
                      id,
                      lang: this.lang
                    });
                  });

                  this.restoreMode();
                });
            }
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
          });
      }
    },
    restoreMode() {
      let mode = this.$route.query.mode ? +this.$route.query.mode : -1;
      if (mode == this.MODE_GALLERY) {
        if (
          this.objectAttributeList.length &&
          this.complexValue &&
          this.complexValue.attributeId
        ) {
          let item = this.objectAttributeList.find(
            attr => attr.attributeId === this.complexValue.attributeId
          );

          if (item) {
            let obj = clone(this.attributeObj);
            obj.props.value = clone(item.value);
            this.selectAttrGallery(obj);
          }
        }
      } else if (mode == this.MODE_PROGRAM) {
        let attrId = service.getActionValueId(this.attributeObj);
        if (attrId) {
          let item = this.objectAttributeList.find(
            attr => attr.attributeId === attrId
          );

          if (item) {
            let obj = clone(this.attributeObj);
            obj.props.value = clone(item.value);
            this.selectAttrProgram(obj);
          }
        }
      } else if (mode == this.MODE_ATTACHMENT) {
        if (
          this.objectAttributeList.length &&
          this.complexValue &&
          this.complexValue.attributeId
        ) {
          let item = this.objectAttributeList.find(
            attr => attr.attributeId === this.complexValue.attributeId
          );

          if (item) {
            let obj = clone(this.attributeObj);
            obj.props.value = clone(item.value);
            this.selectAttrFile(obj);
          }
        }
      }
    },
    async selectedObject(id) {
      this.$store.commit(
        OBJECTS_NAVIGATION_SELECT,
        service.selectNavigation(this.objectsListNavigation, id)
      );
    },
    deleteObject() {
      let name = service.getRemoveObjectName(this.object, this.objectTypes);
      this.$confirm(
        this.$t("object_remove_confirm_message", { name }),
        this.$t("object_remove_confirm_title"),
        {
          confirmButtonText: this.$t("object_remove_confirm_ok"),
          cancelButtonText: this.$t("object_remove_confirm_cancel"),
          type: "warning"
        }
      ).then(() => {
        this.loading = true;
        let urlList = service.getRemoveObjectFiles(this.object);
        this.deleteAllObjectFiles(urlList);
        this.$store
          .dispatch(OBJECTS_DELETE, service.getObjectId(this.object))
          .then(msg => {
            this.parseResponse(msg); // mixins
            this.$store.commit(OBJECTS_CLEAR);
            this.$router.replace({ object: null });
            this.$store
              .dispatch(OBJECTS_LIST_READ, this.lang)
              .then(() => {
                this.loading = false;
              })
              .catch(() => {
                this.loading = false;
              });
          })
          .catch(errStatus => {
            this.parseResponseError(errStatus); // mixins
            this.loading = false;
          });
      });
    },
    addObject() {
      this.isNewObjectCreate = true;
    },
    newObjectFormClose() {
      this.isNewObjectCreate = false;
    },
    newObjectFormSave(val) {
      this.loading = true;
      this.$store
        .dispatch(OBJECTS_CREATE, {
          lang: this.lang,
          data: service.prepareToSave(val)
        })
        .then(msg => {
          this.parseResponse(msg); // mixins
          if (msg.id > 0) {
            this.$router.replace({
              query: { object: msg.id }
            });
            this.$store
              .dispatch(OBJECTS_LIST_READ, this.lang)
              .then(() => {
                this.loading = false;
                this.uploadObject();
              })
              .catch(() => {
                this.loading = false;
              });
          }
        })
        .catch(errStatus => {
          this.parseResponseError(errStatus); // mixins
          this.loading = false;
        });
    },
    curentLanguageChanged() {
      if (this.mode === this.MODE_PROGRAM) {
        programService.changeLanguage(
          this.objectAttributeList,
          this.attributeObj
        );
        this.complexValue = programService.getProgramValueData(
          this.attributeObj
        );
      }
    },
    changeCurrentLanguage(id) {
      this.$store.commit("setCurrentLanguage", id);
    },
    presetFilter() {
      if (!this.objectFilter) {
        return false;
      }

      if (this.objectFilter.countryId) {
        this.filters.countryId = this.objectFilter.countryId;
      }

      if (this.objectFilter.cityId) {
        this.filters.cityId = this.objectFilter.cityId;
      }

      if (this.objectFilter.type) {
        this.filters.type = this.objectFilter.type;
      }

      if (this.objectFilter.content !== undefined) {
        this.filters.content = this.objectFilter.content;
      }
    },
    getFilterStor() {
      this.$store.commit(OBJECTS_FILTER_GET);
    },
    saveFilterStor(obj) {
      this.$store.commit(OBJECTS_FILTER_SAVE, obj);
    },
    changeCard(obj) {
      this.loading = true;
      this.$store
        .dispatch(OBJECTS_UPDATE, {
          id: obj.id,
          lang: this.lang,
          data: service.prepareToUpdate(obj)
        })
        .then(msg => {
          this.parseResponse(msg); // mixins
          this.uploadObject();
          this.loading = false;
        })
        .catch(errStatus => {
          this.parseResponseError(errStatus); // mixins
          this.uploadObject();
          this.loading = false;
        });
    },
    dispatchCRUD(mutation, params) {
      this.loading = true;
      this.$store
        .dispatch(mutation, params)
        .then(msg => {
          this.parseResponse(msg); // mixins
          this.uploadObject();
          this.loading = false;
        })
        .catch(errStatus => {
          this.parseResponseError(errStatus); // mixins
          this.uploadObject();
          this.loading = false;
        });
    },
    executeAction(action, params) {
      this.actionsCRUD
        .filter(item => item.action === action)
        .forEach(item => {
          this.dispatchCRUD(item.mutation, params);
        });
    },
    selectAttrSimple(obj) {
      let { action, params } = service.prepareActionAttr(
        this.object,
        obj,
        this.lang,
        this.currentAtributeLang
      );

      this.executeAction(action, params);
    },
    selectAttrMemo(obj) {
      this.titleMemo = service.objectAttributesName(
        this.objectAttributeList,
        obj
      );
      this.attributeObj = obj;
      this.complexValue = service.getActionValueData(this.attributeObj);

      let requaredField = service.getActionValueRequared(this.attributeObj);
      this.setText(
        service.getActionValuePresetData(this.attributeObj),
        requaredField
      ); // mixins TextEditor

      this.setLinkListForEditor(
        attachmentService.getAttachmentsFromObject(
          this.objectAttributeList,
          this.languages,
          this.$t("content_language")
        )
      ); // mixins TextEditor

      this.setImageListForEditor(
        attachmentService.getAttachmentsImageFromObject(
          this.objectAttributeList,
          this.languages,
          this.$t("content_language")
        )
      ); // mixins TextEditor

      this.showTextEditor = true;
    },
    selectAttrCoord(obj) {
      this.attributeObj = obj;
      this.complexValue = service.getActionValueData(this.attributeObj);
      this.showAttrModalCoord = true;
    },
    selectAttrGallery(obj) {
      this.attributeObj = obj;

      const gallery = galleryService.getGalleryValueData(
        this.attributeObj,
        this.languages
      );

      this.complexValue = gallery;
      let requaredField = service.getActionValueRequared(this.attributeObj);
      this.initSelectedGalleryList(clone(gallery), requaredField); // mixins
      this.setRouteQueryMode(this.MODE_GALLERY);
    },
    selectAttrFile(obj) {
      this.attributeObj = obj;

      const fileTypes = attachmentService.getAttributeValueData(
        this.attributeObj
      );

      this.complexValue = fileTypes;
      let requaredField = service.getActionValueRequared(this.attributeObj);
      this.initSelectedFileList(clone(fileTypes), requaredField); // mixins
      this.setRouteQueryMode(this.MODE_ATTACHMENT);
    },
    setRouteQueryMode(mode) {
      let md = this.$route.query.mode ? +this.$route.query.mode : -1;
      if (md !== mode) {
        this.$router.push({
          query: { ...this.$route.query, mode }
        });
      }
    },
    selectAttrProgram(obj) {
      this.titleMemo = service.objectAttributesName(
        this.objectAttributeList,
        obj
      );

      this.attributeObj = obj;

      let programState = programService.getInitState(
        this.object,
        this.languages,
        service.getActionValueId(obj)
      );
      this.$store.commit("initProgramList", programState);
      this.$store.commit(
        "setRequaredFieldComplexValue",
        service.getActionValueRequared(this.attributeObj)
      );

      this.complexValue = programService.getProgramValueData(this.attributeObj);
      this.setRouteQueryMode(this.MODE_PROGRAM);
    },
    actionAttribute(obj) {
      let type = service.getActionValueType(obj);

      if (systemService.isSimpleType(type)) {
        this.selectAttrSimple(obj);
      } else if (systemService.isMemo(type)) {
        this.selectAttrMemo(obj);
      } else if (systemService.isCoordinate(type)) {
        this.selectAttrCoord(obj);
      } else if (systemService.isGallery(type)) {
        this.selectAttrGallery(obj);
      } else if (systemService.isProgram(type)) {
        this.selectAttrProgram(obj);
      } else if (systemService.isFile(type)) {
        this.selectAttrFile(obj);
      }
    },
    async deleteAttribute(id) {
      let type = service.getDeletedType(this.objectAttributeList, id);
      let val = service.getDeletedValue(this.objectAttributeList, id);

      if (systemService.isGallery(type)) {
        await this.deleteAllObjectFiles(
          galleryService.getUrlListFromValue(val)
        ); // mixins
      } else if (systemService.isFile(type)) {
        await this.deleteAllObjectFiles(
          attachmentService.getUrlListFromValue(val)
        ); // mixins
      }

      this.executeAction(DELETE, { id });
    },
    closeAttrModal() {
      if (this.showAttrModalGallery) {
        this.clearFilesAll(); // mixins
      }

      this.showTextEditor = false;
      this.showAttrModalCoord = false;
      this.showAttrModalGallery = false;
      this.showModalInfo = false;
    },
    saveAttribute(val) {
      let { action, params } = service.prepareActionAttr(
        this.object,
        this.attributeObj,
        this.lang,
        this.currentAtributeLang,
        val
      );

      if (action && params) {
        this.executeAction(action, params);
      }
    },
    saveTextEditor(val) {
      let type = service.getActionValueType(this.attributeObj);
      if (!systemService.isProgram(type)) {
        this.saveAttribute(val);
      }

      this.closeAttrModal();
    },
    changeProgram(callback, callbackCancel) {
      this.showTextEditor = true;
      this.statusDoneCallback = callback;
      this.statusCanceledCallback = callbackCancel;
    },
    cancelProgram() {
      this.setRouteQueryMode(this.MODE_ATTR);
    },
    saveProgram(val) {
      this.saveAttribute(
        programService.prepareToSave(val, this.currentAtributeLang)
      );
    },
    changeRouteMode() {
      let newMode = this.MODE_ATTR;
      if (this.$route.query.mode) {
        if (this.$route.query.mode === this.MODE_PROGRAM) {
          newMode = this.MODE_PROGRAM;
        } else if (this.$route.query.mode === this.MODE_GALLERY) {
          newMode = this.MODE_GALLERY;
        } else if (this.$route.query.mode === this.MODE_ATTACHMENT) {
          newMode = this.MODE_ATTACHMENT;
        }
      }
      this.mode = newMode;
    },
    async saveAttachment() {
      this.loading = true;
      await this.saveEditedAttachment(
        this.object.id,
        this.complexValue.attributeId,
        service.getActionValueData(this.attributeObj).id
      ); // mixins Files
      this.loading = false;

      this.uploadObject();
    },
    cancelAttachment() {
      this.cancelComplexValueMode(); // mixins
      this.setRouteQueryMode(this.MODE_ATTR);
    },
    createGallery() {
      this.galleryMode = MODE_NEW;
      this.editableGalleryObject = {};
      this.showAttrModalGallery = true;
    },
    copyGalleryPhoto() {
      this.mainObjectId = +this.$route.query.object;
      this.copyPhotoMode = true;
      this.setNavigation();
    },
    async saveGallery() {
      this.loading = true;
      await this.saveEditedGallery(
        service.getActionValueData(this.attributeObj).id
      ); // mixins Files
      this.loading = false;

      this.uploadObject();
    },
    cancelGallery() {
      this.cancelComplexValueMode(); // mixins
      this.setRouteQueryMode(this.MODE_ATTR);
    },
    changeGallery() {
      this.galleryMode = MODE_EDIT;
      this.editableGalleryObject = galleryService.getEditableObject(
        this.gallery,
        this.urlEditable
      );
      this.showAttrModalGallery = true;
    },
    clickImagePreview() {
      this.imagePreviewSend();
    },
    async saveGalleryForm(obj) {
      this.loading = true;

      if (this.galleryMode === MODE_EDIT) {
        this.changeGalleryRow(this.urlEditable, obj, this.languages); // mixins
        this.closeAttrModal();
        this.saveGallery();
      } else if (this.galleryMode === MODE_NEW) {
        await this.saveFile(
          service.getObjectId(this.object),
          service.getActionValueId(this.attributeObj),
          obj,
          this.languages,
          this.uploadObject
        ); // mixins
      }

      this.loading = false;
    },
    showObjectInfo() {
      this.$store
        .dispatch(
          OBJECTS_INFO,
          service.objectInfo(this.currentAtributeLang, this.object)
        )
        .then(() => {
          this.showModalInfo = true;
        });
    },
    cancelCopyObject() {
      this.$store.commit(OBJECTS_COPY_CLEAR);
      this.mode = this.MODE_GALLERY;
      let q = clone(this.$route.query);
      delete q.copy_object;
      q.mode = this.mode;
      this.$router.replace({
        query: {
          ...q
        }
      });

      this.restoreMode();
      this.mainObjectId = undefined;
      this.copyPhotoMode = false;
      this.setNavigation();
    },
    copyObjectFiles(files) {
      let promiseList = [];

      files.forEach(file => {
        promiseList.push(
          new Promise(resolve =>
            resolve(
              this.copyFile(
                file,
                this.object,
                this.objectCopy,
                this.currentAtributeLang,
                this.languages,
                this.complexValue.attributeId
              )
            )
          )
        );
      });

      Promise.all(promiseList).then(res => {
        let id = res.find(r => r !== false);
        if (id) {
          this.loading = true;
          this.saveEditedGallery(id).then(() => {
            this.uploadObject();
          });
          this.loading = false;
        }
      });

      this.cancelCopyObject();
    }
  },
  mounted() {
    this.setRouteQueryMode(this.MODE_ATTR);
    this.getFilterStor();
    this.getReferenceLoading();
    this.uploadObject();
    this.setNavigation();
    this.changeCurrentLanguage(this.defaultLangId);
  },
  beforeDestroy() {
    this.$store.commit(OBJECTS_CLEAR);
  },
  watch: {
    "$route.query": function (val, old) {
      if (val.object !== old.object) {
        this.uploadObject();
      }
      if (val.object === undefined) {
        this.$store.commit(OBJECTS_CLEAR);
      }
      if (val.copy_object === undefined) {
        this.resetCopyObject();
      }
      if (val.copy_object !== old.copy_object) {
        this.uploadCopyObject();
      }
      this.changeRouteMode();
    },
    objectFilter: function () {
      this.presetFilter();
      this.setNavigation();
    },
    objectsList: function () {
      this.setNavigation();
    },
    currentAtributeLang: function () {
      this.curentLanguageChanged();
    },
    languages: function () {
      this.changeCurrentLanguage(this.defaultLangId);
    }
  }
};
</script>

<style lang="scss">
@import "@/css/variables.scss";

.home {
  width: 100%;
  height: 100%;
  box-sizing: border-box;

  .home__filter {
    padding: $component_padding_on_top;
    display: flex;
  }

  .home__content {
    width: 100%;
    height: calc(100% - 85px);
    padding-top: $component_padding_on_top;
    display: flex;

    .home__content__object {
      width: 20%;
      height: 100%;

      .home__content__object_action {
        margin: 5px 0;
        display: flex;
        justify-content: space-around;
      }
    }

    .home__content__info {
      width: 80%;
      height: 100%;
      margin-left: $component_padding_on_left;

      .home__content__info-attr {
        margin-top: $component_padding_on_top;
        height: calc(100% - 171px);
      }

      .home__content__no-object {
        width: 100%;
        height: 100%;
      }

      .home__content__info_copy {
        width: 100%;
        height: 100%;
      }
    }
  }

  .exist-complex-value {
    color: $success_color;
  }
}
</style>
